import { isSameSite } from '@hbs-us/contracts';

/**
 * "Which jobs in this list are the same physical site?"
 *
 * `isSameSite` (contracts 2.2.0) stops a NEW duplicate being created at the clock screen. It does
 * nothing about the ones already in the tenant's list — on 2026-07-31 three crew punched in at one
 * driveway inside 108 seconds and the office was left with three jobs to reconcile by hand. This is
 * what finds those, so the office is told rather than having to notice.
 *
 * Matching is on jobName for the same reason `MyClock` matches on it: a punch-created job names
 * itself after its address, and an office-named job ("Moneytree - 113 Dominic Dr") carries one too,
 * so an address-only rule would be blind to every job typed in by hand.
 *
 * IT IS A SUGGESTION, NOT AN AUTHORITY. Nothing here merges anything — a cluster is an invitation
 * to look, and the human decides on the merge screen. Tuned accordingly: `isSameSite` already
 * vetoes two addresses whose building numbers disagree, which is what keeps 220 and 224 Adams
 * apart, and the remaining false positives cost one glance.
 */

/** A job as this grouping needs it — anything carrying an id and a name. */
export interface SiteCandidate {
  id: number;
  jobName: string | null;
}

/**
 * Group jobs into clusters that look like one site, returning ONLY the clusters with more than one
 * member. Order is preserved: clusters appear in the order their first member appeared, and members
 * in the order they arrived, so the list does not reshuffle between renders.
 *
 * Greedy single-pass clustering against each cluster's FIRST member rather than all-pairs. That
 * keeps it O(n·clusters) over a list the screen already holds, and it avoids chaining — without a
 * fixed representative, A~B and B~C would drag A and C into one cluster even when A and C are not
 * the same site, which is exactly how a merge suggestion turns into a wrong merge.
 */
export function groupDuplicateSites<T extends SiteCandidate>(jobs: readonly T[]): T[][] {
  const clusters: T[][] = [];

  for (const job of jobs) {
    const label = job.jobName;
    // A job with no name has nothing to match on and must never anchor a cluster.
    if (!label) continue;

    const home = clusters.find((cluster) => {
      const representative = cluster[0]?.jobName;
      return Boolean(representative) && isSameSite(label, representative as string);
    });

    if (home) home.push(job);
    else clusters.push([job]);
  }

  return clusters.filter((cluster) => cluster.length > 1);
}
